import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Drawer, Select, message, Popconfirm, List } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { InputItem, ButtonItem, InputNumberItem, TextAreaItem, SelectItem, SwitchItem } from "../../../../../Components/Items";

const { Option } = Select;

const TIPOS_NODO = [
  { value: "entrada", label: "Entrada" },
  { value: "salida", label: "Salida" },
  { value: "interseccion", label: "Intersección" },
  { value: "inicio_pasillo", label: "Inicio de pasillo" },
  { value: "fin_pasillo", label: "Fin de pasillo" },
  { value: "punto_intermedio", label: "Punto intermedio" },
  { value: "acceso_ubicaciones", label: "Acceso a ubicaciones" },
  { value: "estacion", label: "Estación de surtido" },
  { value: "puerta", label: "Puerta" },
  { value: "rampa", label: "Rampa" },
  { value: "elevador", label: "Elevador" },
  { value: "transicion_seccion", label: "Transición entre secciones" },
];

//Panel lateral para editar un nodo del mapa (capa de circulación). Los racks ya no se crean/administran
//desde aquí (ver PanelPropiedadesRack.js) - un rack se dibuja aparte en el lienzo y se vincula a este (u
//otros) nodo arrastrando una conexión. Aquí solo se muestra, de forma informativa, qué racks alcanzan
//este punto.
const PanelPropiedadesNodo = ({
  visible,
  onClose,
  nodo,
  secciones,
  headersToken,
  onGuardado,
  onEliminado,
}) => {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState("punto_intermedio");
  const [seccionAlmacenId, setSeccionAlmacenId] = useState(undefined);
  const [bloqueado, setBloqueado] = useState(false);
  const [instrucciones, setInstrucciones] = useState("");
  const [tiempoCruceSegundos, setTiempoCruceSegundos] = useState(undefined);
  const [guardando, setGuardando] = useState(false);
  const [racksDelNodo, setRacksDelNodo] = useState([]);

  useEffect(() => {
    if (nodo) {
      setCodigo(nodo.codigo || "");
      setNombre(nodo.nombre || "");
      setTipo(nodo.tipo || "punto_intermedio");
      setSeccionAlmacenId(nodo.seccionAlmacenId ? String(nodo.seccionAlmacenId) : undefined);
      setBloqueado(!!nodo.bloqueado);
      setInstrucciones(nodo.transicion?.instrucciones || "");
      setTiempoCruceSegundos(nodo.transicion?.tiempoCruceSegundos);
    }
  }, [nodo]);

  const cargarRacksDelNodo = useCallback(async () => {
    if (!nodo?._id) return;
    try {
      const res = await axios.post("rack/selectRacksDeNodo", { nodoId: nodo._id }, { headers: headersToken });
      if (res.data.success === 2) setRacksDelNodo(res.data.racks || []);
    } catch (error) {
      console.error("Error al cargar los racks del nodo:", error);
    }
  }, [nodo, headersToken]);

  useEffect(() => {
    if (visible) cargarRacksDelNodo();
  }, [visible, cargarRacksDelNodo]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await axios.post(
        "mapaAlmacen/editarNodoMapa",
        {
          _id: nodo._id,
          codigo,
          nombre,
          tipo,
          seccionAlmacenId,
          bloqueado,
          transicion: tipo === "transicion_seccion" ? { instrucciones, tiempoCruceSegundos } : undefined,
        },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success("Nodo actualizado correctamente");
        onGuardado();
      } else {
        message.error(res.data.error || "No se pudo actualizar el nodo");
      }
    } catch (error) {
      console.error("Error al actualizar el nodo:", error);
      message.error("No se pudo actualizar el nodo");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    try {
      const res = await axios.post("mapaAlmacen/eliminarNodoMapa", { _id: nodo._id }, { headers: headersToken });
      if (res.data.success === 2) {
        message.success("Nodo eliminado correctamente");
        onEliminado();
      } else {
        message.error(res.data.error || "No se pudo eliminar el nodo");
      }
    } catch (error) {
      console.error("Error al eliminar el nodo:", error);
      message.error("No se pudo eliminar el nodo");
    }
  };

  if (!nodo) return null;

  return (
    <Drawer title="Propiedades del nodo" visible={visible} onClose={onClose} width={380}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Código</div>
        <InputItem value={codigo} onChange={(e) => setCodigo(e.target.value)} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Nombre</div>
        <InputItem value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Tipo</div>
        <SelectItem width="100%" value={tipo} onChange={setTipo}>
          {TIPOS_NODO.map((valorTipo) => (
            <Option key={valorTipo.value} value={valorTipo.value}>
              {valorTipo.label}
            </Option>
          ))}
        </SelectItem>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Sección (opcional)</div>
        <SelectItem width="100%" allowClear value={seccionAlmacenId} onChange={setSeccionAlmacenId}>
          {(secciones || []).map((valorSeccion) => (
            <Option key={valorSeccion._id} value={String(valorSeccion._id)}>
              {valorSeccion.nombre}
            </Option>
          ))}
        </SelectItem>
      </div>

      {tipo === "transicion_seccion" && (
        <>
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, color: "#6b7280" }}>Instrucciones</div>
            <TextAreaItem rows={2} value={instrucciones} onChange={(e) => setInstrucciones(e.target.value)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4, color: "#6b7280" }}>Tiempo de cruce (segundos)</div>
            <InputNumberItem style={{ width: "100%" }} min={0} value={tiempoCruceSegundos} onChange={setTiempoCruceSegundos} />
          </div>
        </>
      )}

      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <SwitchItem checked={bloqueado} onChange={setBloqueado} />
        <span style={{ color: "#6b7280" }}>Bloqueado (excluido de las rutas)</span>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <Popconfirm title="¿Eliminar este nodo?" onConfirm={eliminar} okText="Eliminar" cancelText="Cancelar">
          <ButtonItem danger icon={<DeleteOutlined />}>
            Eliminar
          </ButtonItem>
        </Popconfirm>
        <ButtonItem type="primary" loading={guardando} onClick={guardar}>
          Guardar
        </ButtonItem>
      </div>

      <div style={{ fontWeight: 700, marginBottom: 8 }}>Racks que se alcanzan desde aquí ({racksDelNodo.length})</div>
      <List
        size="small"
        dataSource={racksDelNodo}
        locale={{ emptyText: "Ningún rack vinculado a este nodo todavía" }}
        renderItem={(valorRack) => <List.Item>{valorRack.codigo}</List.Item>}
      />
    </Drawer>
  );
};

export default PanelPropiedadesNodo;
